<template>
  <div class="home">
    <!-- Hero Section -->
    <section class="hero">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">Your Global Radio Companion</h1>
          <p class="hero-subtitle">Immerse yourself in a world of endless audio entertainment. From chart-topping hits to breaking news, sports commentary to cultural programs – all at your fingertips. Join millions of listeners worldwide and experience radio like never before.</p>
          <div class="hero-buttons">
            <a href="/#download" class="btn btn-primary">Start Listening Now</a>
            <a href="/#features" class="btn btn-outline">Discover More</a>
          </div>
          <div class="hero-stats">
            <div class="stat-item">
              <div class="stat-number">24/7</div>
              <div class="stat-label">Non-stop Listening</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">HD</div>
              <div class="stat-label">High Quality</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">50K+</div>
              <div class="stat-label">Radio Stations</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">100+</div>
              <div class="stat-label">Countries</div>
            </div>
          </div>
        </div>
      </div>
      <div class="hero-background"></div>
      <div class="hero-pattern"></div>
    </section>

    <!-- App Showcase -->
    <section id="image-carousel" class="section showcase-section">
      <div class="container">
        <div class="section-header">
          <span class="section-badge">App Showcase</span>
          <h2 class="section-title">Beautiful Interface</h2>
          <p class="section-subtitle">Experience our sleek and intuitive design that makes radio listening a pleasure</p>
        </div>
        <div class="showcase-container">
          <swiper
            :slides-per-view="slidesPerView"
            :space-between="2"
            :loop="true"
            :pagination="{
              clickable: true,
              el: '.swiper-pagination'
            }"
            :navigation="false"
            @swiper="setSwiper"
            class="mySwiper"
          >
            <swiper-slide v-for="(image, index) in images" :key="index">
              <div class="carousel-item">
                <img :src="image.src" :alt="image.alt" class="showcase-image">
              </div>
            </swiper-slide>
            <div class="swiper-pagination"></div>
          </swiper>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="section">
      <div class="container">
        <div class="image-nav">
          <div 
            class="nav-dot" 
            :class="{ active: currentSlide === 0 || currentSlide === 1 }" 
            @click="goToSlide(0)"
          ></div>
          <div 
            class="nav-dot" 
            :class="{ active: currentSlide === 2 }" 
            @click="goToSlide(2)"
          ></div>
        </div>
        <div class="section-header">
          <span class="section-badge">Core Features</span>
          <h2 class="section-title">Premium Features</h2>
          <p class="section-subtitle">Experience the next generation of radio listening with our cutting-edge features</p>
        </div>
        <div class="features-grid">
          <div class="feature-card card">
            <div class="feature-icon">🎵</div>
            <h3>Global Radio Stations</h3>
            <p>Access an extensive collection of over 50,000 radio stations worldwide, covering music, news, sports, talk shows, and more. Your gateway to global entertainment.</p>
          </div>
          <div class="feature-card card">
            <div class="feature-icon">🔍</div>
            <h3>Smart Search</h3>
            <p>Find your perfect station with our intelligent search system. Filter by genre, language, country, or popularity to discover new favorites.</p>
          </div>
          <div class="feature-card card">
            <div class="feature-icon">⭐</div>
            <h3>Personalized Favorites</h3>
            <p>Create your own collection of favorite stations. Organize them into custom playlists and access them instantly with just one tap.</p>
          </div>
          <div class="feature-card card">
            <div class="feature-icon">🎧</div>
            <h3>Premium Audio Quality</h3>
            <p>Enjoy studio-grade audio streaming with our advanced technology. Experience crystal-clear sound with minimal buffering, even on slower connections.</p>
          </div>
          <div class="feature-card card">
            <div class="feature-icon">📊</div>
            <h3>Real-time Analytics</h3>
            <p>Track your listening habits and discover new content with our intelligent analytics. Get personalized recommendations based on your preferences.</p>
          </div>
          <div class="feature-card card">
            <div class="feature-icon">🌐</div>
            <h3>Global Community</h3>
            <p>Connect with radio enthusiasts worldwide. Share your favorite stations, discover new content, and join discussions about your favorite programs.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Download Section -->
    <section id="download" class="section download-section">
      <div class="container">
        <div class="download-content glass-effect">
          <div class="section-header">
            <span class="section-badge pulse-badge">Download Now</span>
            <h2 class="section-title download-title">Start Listening Today</h2>
            <p class="section-subtitle">Join millions of users worldwide who are already enjoying their favorite radio stations with our app</p>
          </div>
          <div class="download-grid">
            <div class="download-buttons">
              <button class="btn btn-primary download-btn android-btn" @click="showAndroidTip">
                <div class="btn-icon">
                  <i class="fab fa-google-play"></i>
                </div>
                <div class="btn-text">
                  <span class="small-text">Get it on</span>
                  <span class="large-text">Google Play</span>
                </div>
              </button>
              <a href="https://apps.apple.com/us/app/radio-app-fm-radio-stations/id6648771859" class="btn btn-primary download-btn ios-btn">
                <div class="btn-icon">
                  <i class="fab fa-apple"></i>
                </div>
                <div class="btn-text">
                  <span class="small-text">Download on</span>
                  <span class="large-text">App Store</span>
                </div>
              </a>
            </div>
          </div>
          <p class="download-note">Free to download. Premium features available with subscription.</p>
        </div>
      </div>
    </section>

    <!-- 弹窗组件 -->
    <div class="modal" v-if="showModal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Notice</h3>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <i class="fas fa-info-circle"></i>
          <p>Coming soon! We are working hard to release the Android version.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/pagination'

export default {
  name: 'HomeView',
  components: {
    Swiper,
    SwiperSlide
  },
  data() {
    return {
      swiper: null,
      slidesPerView: 1,
      currentSlide: 0,
      images: [
        { src: require('../assets/1.DO13dmgF.png'), alt: 'App Screenshot 1' },
        { src: require('../assets/2.CzG4TrlX.png'), alt: 'App Screenshot 2' },
        { src: require('../assets/3.sd8PDrbJ.png'), alt: 'App Screenshot 3' },
        { src: require('../assets/4.BELGbizj.png'), alt: 'App Screenshot 4' },
        { src: require('../assets/5.o1f3WFG9.png'), alt: 'App Screenshot 5' },
        { src: require('../assets/6.CBfAjPOR.png'), alt: 'App Screenshot 6' }
      ],
      showModal: false,
      modalStyle: {
        top: '0px',
        left: '0px'
      }
    }
  },
  mounted() {
    this.updateSlidesPerView()
    window.addEventListener('resize', this.updateSlidesPerView)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateSlidesPerView)
  },
  methods: {
    setSwiper(swiper) {
      this.swiper = swiper
      this.swiper.on('slideChange', () => {
        this.currentSlide = this.swiper.realIndex % 3
      })
    },
    goToSlide(index) {
      if (this.swiper) {
        this.swiper.slideTo(index)
        this.currentSlide = index
        // 滚动到图片位置
        const showcaseSection = document.getElementById('image-carousel')
        if (showcaseSection) {
          const offset = showcaseSection.offsetTop - 100
          window.scrollTo({
            top: offset,
            behavior: 'auto'
          })
        }
      }
    },
    updateSlidesPerView() {
      if (window.innerWidth >= 1200) {
        this.slidesPerView = 3
      } else if (window.innerWidth >= 768) {
        this.slidesPerView = 2
      } else {
        this.slidesPerView = 1
      }
    },
    showAndroidTip() {
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    }
  }
}
</script>

<style scoped>
.hero {
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  padding-top: 60px;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%);
  z-index: 0;
}

.hero-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.4;
  z-index: 0;
}

.hero-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: #1e293b;
  line-height: 1.2;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #64748b;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.hero-buttons {
  display: flex;
  gap: 1.2rem;
  justify-content: center;
  margin-bottom: 3rem;
  position: relative;
  z-index: 5;
}

.hero-buttons .btn {
  min-width: 180px;
  font-size: 1.05rem;
  padding: 15px 32px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.hero-buttons .btn-primary {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.hero-buttons .btn-primary::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0));
  border-radius: 12px 12px 0 0;
  z-index: -1;
}

.hero-buttons .btn-primary:hover {
  box-shadow: 
    0 15px 30px rgba(74, 134, 232, 0.4),
    0 5px 15px rgba(0, 0, 0, 0.08);
  transform: translateY(-4px);
}

.hero-buttons .btn-outline {
  border-width: 2px;
  background: rgba(255, 255, 255, 0.9);
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 0.5rem;
}

.stat-label {
  font-size: 0.9rem;
  color: #64748b;
  text-align: center;
}

.section-header {
  text-align: center;
  margin-bottom: 2rem;
  margin-top: 100px;
}

.section-badge {
  display: inline-block;
  padding: 6px 12px;
  background: #f1f5f9;
  color: var(--primary-color);
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 16px;
  border: 1px solid rgba(74, 134, 232, 0.2);
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.feature-card {
  padding: 2rem;
  text-align: center;
  border: 1px solid rgba(74, 134, 232, 0.1);
  background: white;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(74, 134, 232, 0.1);
}

.feature-icon {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.feature-card h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--primary-color);
}

.feature-card p {
  color: #64748b;
  line-height: 1.6;
}

.showcase-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding-top: 2rem;
}

.showcase-container {
  margin-top: 1rem;
  position: relative;
}

.carousel-item {
  padding: 1rem;
}

.showcase-image {
  width: 100%;
  height: auto;
  border-radius: var(--border-radius);
}

.showcase-image:hover {
  transform: scale(1.02);
}

/* Swiper 分页样式 */
:deep(.swiper-pagination) {
  position: relative;
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  gap: 12px;
}

:deep(.swiper-pagination-bullet) {
  width: 12px;
  height: 12px;
  background: #cbd5e1;
  opacity: 0.5;
  border-radius: 50%;
  cursor: pointer;
}

:deep(.swiper-pagination-bullet-active) {
  background: var(--primary-color);
  opacity: 1;
  transform: scale(1.2);
}

:deep(.swiper-pagination-bullet:hover) {
  opacity: 0.8;
  transform: scale(1.1);
}

.download-section {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding-top: 2rem;
  position: relative;
  overflow: hidden;
}

.download-section::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -20%;
  width: 70%;
  height: 200%;
  background: radial-gradient(ellipse at center, rgba(74, 134, 232, 0.08) 0%, transparent 70%);
  transform: rotate(-30deg);
  pointer-events: none;
}

.download-section::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 70%;
  height: 200%;
  background: radial-gradient(ellipse at center, rgba(74, 134, 232, 0.05) 0%, transparent 70%);
  transform: rotate(30deg);
  pointer-events: none;
}

.download-content {
  text-align: center;
  padding: 4rem;
  border-radius: var(--border-radius);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.8) inset,
    0 1px 3px rgba(255, 255, 255, 0.3) inset;
  position: relative;
  overflow: hidden;
}

.download-title {
  background: linear-gradient(135deg, #1e293b, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
  font-size: 2.8rem;
}

.download-grid {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 3rem auto;
  max-width: 600px;
}

.download-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  position: relative;
  z-index: 1;
  width: 100%;
  gap: 5px;
}

.download-btn {
  width: 220px;
  height: 60px;
  box-sizing: border-box;
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 14px;
  position: relative;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  text-decoration: none;
  overflow: hidden;
  margin: 0 auto;
}

.download-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.download-btn:hover::after {
  opacity: 1;
}

.download-btn:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 
    0 15px 30px rgba(74, 134, 232, 0.3),
    0 5px 15px rgba(0, 0, 0, 0.05);
}

.download-btn:active {
  transform: translateY(0) scale(0.98);
}

.android-btn {
  background: linear-gradient(135deg, #4CAF50 0%, #3d8b40 100%);
  box-shadow: 
    0 10px 20px rgba(76, 175, 80, 0.3),
    0 3px 6px rgba(0, 0, 0, 0.1),
    0 1px 0 rgba(255, 255, 255, 0.1) inset;
  margin-right: 5px;
  z-index: 2;
}

.android-btn:hover {
  box-shadow: 
    0 15px 30px rgba(76, 175, 80, 0.4),
    0 5px 10px rgba(0, 0, 0, 0.1),
    0 1px 0 rgba(255, 255, 255, 0.2) inset;
}

.ios-btn {
  background: linear-gradient(135deg, #007AFF 0%, #0051d1 100%);
  box-shadow: 
    0 10px 20px rgba(0, 122, 255, 0.3),
    0 3px 6px rgba(0, 0, 0, 0.1),
    0 1px 0 rgba(255, 255, 255, 0.1) inset;
  margin-left: 5px;
  z-index: 1;
}

.ios-btn:hover {
  box-shadow: 
    0 15px 30px rgba(0, 122, 255, 0.4),
    0 5px 10px rgba(0, 0, 0, 0.1),
    0 1px 0 rgba(255, 255, 255, 0.2) inset;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-right: 5px;
  width: 36px;
  flex-shrink: 0;
}

.btn-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  line-height: 1.1;
}

.small-text {
  font-size: 0.7rem;
  font-weight: 400;
  opacity: 0.9;
}

.large-text {
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.download-note {
  margin-top: 1rem;
  color: #64748b;
  font-size: 0.9rem;
}

@media (max-width: 992px) {
  .download-grid {
    margin: 2rem auto;
  }
}

.image-nav {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 2rem;
  margin-top: -150px;
  position: relative;
  z-index: 10;
}

.nav-dot {
  width: 12px;
  height: 12px;
  background: #cbd5e1;
  border-radius: 50%;
  cursor: pointer;
  opacity: 0.5;
}

.nav-dot:hover {
  opacity: 0.8;
  transform: scale(1.1);
}

.nav-dot.active {
  background: var(--primary-color);
  opacity: 1;
  transform: scale(1.2);
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  padding: 1rem;
}

.modal-content {
  position: relative;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 24px;
  width: 380px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.8) inset,
    0 2px 3px rgba(255, 255, 255, 0.3) inset;
  transform: translateY(0);
  animation: modal-appear 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  backdrop-filter: blur(10px);
  overflow: hidden;
  background-image: 
    linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 100%),
    radial-gradient(circle at top right, rgba(74, 134, 232, 0.08), transparent 60%);
}

.modal-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(241, 245, 249, 0.6);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.6) 100%);
}

.modal-header h3 {
  margin: 0;
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  background: linear-gradient(135deg, #1e293b, #334155);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.close-btn {
  background: rgba(255, 255, 255, 0.8);
  border: none;
  font-size: 1.5rem;
  color: #94a3b8;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-right: -0.5rem;
}

.close-btn:hover {
  color: #1e293b;
  background: white;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  transform: scale(1.05);
}

.close-btn:active {
  transform: scale(0.95);
}

.modal-body {
  padding: 2rem;
  text-align: center;
  position: relative;
  background: white;
  overflow: hidden;
}

.modal-body::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle at center, rgba(74, 134, 232, 0.03) 0%, transparent 70%);
  z-index: 0;
}

.modal-body i {
  font-size: 3.5rem;
  margin-bottom: 1.25rem;
  position: relative;
  z-index: 1;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 2px 4px rgba(59, 134, 232, 0.3));
}

.modal-body p {
  margin: 0;
  color: #334155;
  font-size: 1.125rem;
  line-height: 1.6;
  position: relative;
  z-index: 1;
  font-weight: 500;
}

@keyframes modal-appear {
  0% {
    transform: translateY(30px) scale(0.95);
    opacity: 0;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

.hero-buttons a,
.download-buttons a {
  text-decoration: none;
}

.pulse-badge {
  animation: pulse-animation 2s infinite;
  position: relative;
}

@keyframes pulse-animation {
  0% {
    box-shadow: 0 0 0 0 rgba(74, 134, 232, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(74, 134, 232, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(74, 134, 232, 0);
  }
}

@media (max-width: 768px) {
  .hero-content {
    padding: 25px;
    margin: 0 15px;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-buttons {
    flex-direction: column;
  }
  
  .hero-stats {
    gap: 1.5rem;
  }
  
  .stat-item {
    min-width: 100px;
  }
  
  .stat-number {
    font-size: 1.5rem;
  }
  
  .stat-label {
    font-size: 0.8rem;
  }
  
  .download-section {
    padding: 1rem;
  }
  
  .download-content {
    padding: 2rem 1rem;
  }
  
  .download-title {
    font-size: 2rem;
  }
  
  .download-buttons {
    flex-direction: column;
    width: 100%;
    align-items: center;
    gap: 0.4rem;
  }
  
  .android-btn, .ios-btn {
    margin: 0;
  }
  
  .btn-text {
    align-items: center;
    text-align: center;
    line-height: 1.2;
    width: 100%;
  }
  
  .btn-icon {
    margin-right: 0;
    margin-bottom: 2px;
    width: auto;
    font-size: 1.4rem;
  }
  
  .download-btn {
    flex-direction: column;
    padding: 0.7rem 1rem;
  }
  
  .small-text {
    font-size: 0.6rem;
  }
  
  .large-text {
    font-size: 0.85rem;
  }
  
  .feature-card {
    padding: 1.5rem;
  }
}
</style> 